// Scaling runs for Forecasting > Scaling Results, from the metrics lambda
// (bpm-lambdas#8978): GET /getScalingAnalyses lists the company's runs (newest
// first, with status) and GET /getScalingResults loads one run's results JSON.
import { useQuery } from '@tanstack/react-query';
import { awaitJSON, MetricsLambdaFetch } from '../../utils/fetch-utils';
import { ScalingRunFile } from './SaturationCurve/scalingData';
import {
  GetScalingAnalysesResponse,
  GetScalingResultsResponse,
  ScalingAnalysis,
  ScalingAnalysisStatus
} from './scalingAnalysisContract';

// Same company-scoped param the other Forecasting lambda calls send.
const TEST_COMPANY_PAGE_KEY = 'FORECASTING';

/** Enough of econ's `{id}_scaling.json` (design 5.5) to render: a channel and a non-empty combos array. */
export const isScalingRunFile = (value: unknown): value is ScalingRunFile =>
  typeof value === 'object' &&
  value !== null &&
  'channel' in value &&
  typeof value.channel === 'string' &&
  'combos' in value &&
  Array.isArray(value.combos) &&
  value.combos.length > 0;

export const fetchScalingAnalyses = async (company: string): Promise<ScalingAnalysis[]> => {
  const res = await MetricsLambdaFetch('/getScalingAnalyses', {
    params: { company, testCompanyPageKey: TEST_COMPANY_PAGE_KEY }
  });
  const { scalingAnalyses } = await awaitJSON<GetScalingAnalysesResponse>(res);
  return scalingAnalyses ?? [];
};

/** What the tab renders for one run; `error` = a Completed run whose JSON isn't renderable. */
export type ScalingResultState =
  | { status: typeof ScalingAnalysisStatus.IN_PROGRESS }
  | { status: typeof ScalingAnalysisStatus.FAILED; error: string }
  | { status: typeof ScalingAnalysisStatus.COMPLETED; run: ScalingRunFile }
  | { status: 'error'; message: string };

/** One run's results, mapped to what the tab renders. */
export const fetchScalingResult = async (
  company: string,
  scalingId: string
): Promise<ScalingResultState> => {
  const res = await MetricsLambdaFetch('/getScalingResults', {
    params: { company, scalingId, testCompanyPageKey: TEST_COMPANY_PAGE_KEY }
  });
  const { status, result, error } = await awaitJSON<GetScalingResultsResponse>(res);
  if (status === ScalingAnalysisStatus.COMPLETED) {
    return isScalingRunFile(result)
      ? { status: ScalingAnalysisStatus.COMPLETED, run: result }
      : { status: 'error', message: "This run's results file couldn't be read." };
  }
  if (status === ScalingAnalysisStatus.FAILED) {
    return {
      status: ScalingAnalysisStatus.FAILED,
      error: error ?? 'No error message was recorded.'
    };
  }
  return { status: ScalingAnalysisStatus.IN_PROGRESS };
};

// While anything is still running, re-check every 30s (the completion email is
// the other signal); finished runs don't change, so nothing polls then.
const POLL_MS = 30_000;

export const scalingAnalysesQueryKey = (company: string | undefined) => [
  'scalingAnalyses',
  company
];

/** The company's runs, newest first (at most the 100 most recent, per the lambda). */
export const useScalingRuns = (company: string | undefined) =>
  useQuery<ScalingAnalysis[]>({
    queryKey: scalingAnalysesQueryKey(company),
    queryFn: () => {
      if (!company) {
        throw new Error('No company selected.');
      }
      return fetchScalingAnalyses(company);
    },
    enabled: Boolean(company),
    // A run requested a moment ago must show up when the tab opens.
    staleTime: 0,
    refetchOnMount: true,
    refetchInterval: query =>
      query.state.data?.some(run => run.status === 'In Progress') ? POLL_MS : false,
    // ScalingResults reports its own load errors (and a failed background poll shouldn't).
    meta: { suppressGlobalError: true }
  });

/**
 * One run's results. A Completed run never changes, so it stays cached;
 * anything else re-checks on mount/refresh (#8978 lets a Failed run turn
 * Completed: its results file wins over a leftover error file), and an open
 * In Progress run polls.
 */
export const useScalingResult = (company: string | undefined, scalingId: string | undefined) =>
  useQuery<ScalingResultState>({
    queryKey: ['scalingResult', company, scalingId],
    queryFn: () => {
      if (!company || !scalingId) {
        throw new Error('No scaling run selected.');
      }
      return fetchScalingResult(company, scalingId);
    },
    enabled: Boolean(company && scalingId),
    staleTime: query =>
      query.state.data?.status === ScalingAnalysisStatus.COMPLETED ? Infinity : 0,
    refetchOnMount: true,
    refetchInterval: query =>
      query.state.data?.status === ScalingAnalysisStatus.IN_PROGRESS ? POLL_MS : false,
    meta: { suppressGlobalError: true }
  });
